import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import MobileLayout from '../../components/MobileLayout'
import { employeeApi } from '../../lib/employeeApi'
import { useEmployeeModules } from '../../lib/modules'

interface Shift {
  id: string
  date: string
  startTime: string
  endTime: string
  breakMinutes: number
  position?: { name: string; color: string }
  note?: string
}

const DAYS = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam']
const MONTHS = ['jan', 'fév', 'mar', 'avr', 'mai', 'juin', 'juil', 'août', 'sep', 'oct', 'nov', 'déc']

function formatDate(dateStr: string) {
  const d = new Date(dateStr)
  return `${DAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`
}

function netHours(s: Shift) {
  const [sh, sm] = s.startTime.split(':').map(Number)
  const [eh, em] = s.endTime.split(':').map(Number)
  return ((eh * 60 + em) - (sh * 60 + sm) - s.breakMinutes) / 60
}

interface Badges {
  messages: number; planning: number; absences: number; documents: number
  timesheets: number; notifications: number; missions: number; tasks: number
}

export default function MobileHome() {
  const nav = useNavigate()
  const [shifts, setShifts] = useState<Shift[]>([])
  const [loading, setLoading] = useState(true)
  const [today, setToday] = useState(() => new Date().toISOString().split('T')[0])
  const [badgeEnabled, setBadgeEnabled] = useState(true)
  const { modules } = useEmployeeModules()
  const [badges, setBadges] = useState<Badges>({ messages: 0, planning: 0, absences: 0, documents: 0, timesheets: 0, notifications: 0, missions: 0, tasks: 0 })

  useEffect(() => {
    Promise.all([
      employeeApi.get('/employee/me/shifts'),
      employeeApi.get('/employee/me/server-time'),
      employeeApi.get('/employee/me'),
      employeeApi.get('/employee/me/badges'),
    ]).then(([shiftsR, timeR, meR, badgesR]) => {
      setShifts(shiftsR.data.slice(0, 20))
      setToday(timeR.data.today)
      setBadgeEnabled(meR.data?.establishment?.badgeEnabled !== false)
      setBadges(badgesR.data)
    }).catch(e => {
      console.error('Failed to load:', e)
      setShifts([])
    }).finally(() => setLoading(false))
  }, [])
  const todayShifts = shifts.filter(s => s.date === today)
  // Prochains jours travaillés : un bloc par jour (journée coupée = 2 horaires sur la même ligne)
  const upcomingDays = [...new Set(shifts.filter(s => s.date > today).map(s => s.date))].slice(0, 5)
    .map(date => ({ date, list: shifts.filter(s => s.date === date) }))

  const ACTIONS = [
    ...(badgeEnabled && modules.badgeuse ? [{ icon: '⏱', label: 'Pointer', color: 'bg-blue-500', to: '/app/clock', badge: 0 }] : []),
    { icon: '📅', label: 'Mes horaires',      color: 'bg-indigo-500', to: '/app/planning',     badge: badges.planning },
    { icon: '✅', label: 'UtileClean',        color: 'bg-teal-500',   to: '/app/tasks',        badge: badges.tasks },
    { icon: '🎯', label: 'Missions',          color: 'bg-purple-500', to: '/app/missions',     badge: badges.missions },
    { icon: '⚖️', label: 'BVP',             color: 'bg-yellow-600', to: '/app/bvp',          badge: 0 },
    { icon: '🚚', label: '+ Livraison',       color: 'bg-orange-600', to: '/app/livraison',    badge: 0 },
    ...(modules.conges ? [{ icon: '🌴', label: 'Congés', color: 'bg-green-500', to: '/app/absences', badge: badges.absences }] : []),
    { icon: '📋', label: 'Fiches de poste',   color: 'bg-slate-600',  to: '/app/job-sheets',   badge: 0 },
    ...(modules.rh ? [
      { icon: '📄', label: 'Documents',         color: 'bg-orange-500', to: '/app/documents',    badge: badges.documents },
      { icon: '💶', label: 'Bulletins de paie', color: 'bg-emerald-600',to: '/app/payslips',     badge: 0 },
      { icon: '📋', label: 'Feuilles d\'heures',color: 'bg-pink-500',   to: '/app/timesheets',   badge: badges.timesheets },
    ] : []),
    { icon: '🔔', label: 'Notifications',     color: 'bg-red-500',    to: '/app/notifications', badge: badges.notifications },
    { icon: '💬', label: 'Messages',          color: 'bg-blue-500',   to: '/app/messages',     badge: badges.messages },
  ]

  async function forceSync() {
    // Vide le cache du service worker puis recharge la page
    if ('serviceWorker' in navigator && 'caches' in window) {
      const keys = await caches.keys()
      await Promise.all(keys.map(k => caches.delete(k)))
    }
    window.location.reload()
  }

  return (
    <MobileLayout>
      <div className="p-4 space-y-4">

        {/* Today card */}
        <div className="bg-blue-600 rounded-2xl p-4 text-white">
          <div className="text-blue-200 text-sm mb-1">
            {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
          </div>
          {todayShifts.length > 0 ? (
            <div>
              <div className="text-2xl font-bold">{todayShifts.map(t => `${t.startTime} – ${t.endTime}`).join('  ·  ')}</div>
              <div className="text-blue-200 mt-1 flex items-center gap-2">
                {todayShifts[0].position && (
                  <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">
                    {todayShifts[0].position.name}
                  </span>
                )}
                <span className="text-xs">{todayShifts.reduce((a, t) => a + netHours(t), 0).toFixed(1)}h</span>
              </div>
            </div>
          ) : (
            <div className="text-xl font-semibold text-blue-100">Pas d'horaire aujourd'hui</div>
          )}
        </div>

        {/* Planning de toute l'équipe (comme le tableau Excel) */}
        <button onClick={() => nav('/app/team')}
          className="w-full bg-white rounded-2xl p-4 shadow-sm flex items-center gap-3 text-left active:scale-[0.99]">
          <div className="w-11 h-11 bg-indigo-500 rounded-xl flex items-center justify-center text-2xl">📋</div>
          <div className="flex-1">
            <div className="font-semibold text-gray-800">Planning de l’équipe</div>
            <div className="text-sm text-gray-500">Toute la semaine, comme le tableau du magasin</div>
          </div>
          <span className="text-gray-300 text-xl">›</span>
        </button>

        {/* Mes tâches du jour */}
        <button onClick={() => nav('/app/tasks')}
          className="w-full bg-white rounded-2xl p-4 shadow-sm flex items-center gap-3 text-left active:scale-[0.99]">
          <div className="w-11 h-11 bg-teal-500 rounded-xl flex items-center justify-center text-2xl">✅</div>
          <div className="flex-1">
            <div className="font-semibold text-gray-800">Mes tâches du jour</div>
            <div className="text-sm text-gray-500">
              {loading ? 'Chargement…' : badges.tasks > 0 ? `${badges.tasks} à faire` : 'Rien à faire pour l’instant'}
            </div>
          </div>
          {badges.tasks > 0 && <span className="min-w-[28px] h-7 px-2 bg-red-500 text-white text-sm font-bold rounded-full flex items-center justify-center">{badges.tasks}</span>}
        </button>

        {/* Quick actions */}
        <div>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Actions rapides</h2>
          <div className="grid grid-cols-3 gap-3">
            {ACTIONS.map(a => (
              <button
                key={a.to}
                onClick={() => nav(a.to)}
                className="flex flex-col items-center gap-2 p-3 bg-white rounded-2xl shadow-sm hover:shadow-md transition-all active:scale-95"
              >
                <div className="relative">
                  <div className={`w-12 h-12 ${a.color} rounded-xl flex items-center justify-center text-2xl`}>
                    {a.icon}
                  </div>
                  {a.badge > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none px-1">
                      {a.badge > 99 ? '99+' : a.badge}
                    </span>
                  )}
                </div>
                <span className="text-xs text-gray-600 font-medium text-center leading-tight">{a.label}</span>
              </button>
            ))}
            {/* Bouton Synchroniser */}
            <button
              onClick={forceSync}
              className="flex flex-col items-center gap-2 p-3 bg-white rounded-2xl shadow-sm hover:shadow-md transition-all active:scale-95"
            >
              <div className="w-12 h-12 bg-gray-400 rounded-xl flex items-center justify-center text-2xl">
                🔄
              </div>
              <span className="text-xs text-gray-600 font-medium text-center leading-tight">Synchroniser</span>
            </button>
          </div>
        </div>

        {/* Prochains horaires */}
        <div>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Mes prochains horaires</h2>
          {loading ? (
            <div className="text-center py-8 text-gray-400">Chargement...</div>
          ) : upcomingDays.length === 0 ? (
            <div className="text-center py-6 text-gray-400 bg-white rounded-2xl">Aucun horaire à venir</div>
          ) : (
            <div className="space-y-2">
              {upcomingDays.map(d => (
                <div key={d.date} className="bg-white rounded-2xl p-4 shadow-sm flex items-center justify-between">
                  <div>
                    <div className="font-medium text-gray-800">{formatDate(d.date)}</div>
                    <div className="text-sm text-gray-500">{d.list.map(s => `${s.startTime} – ${s.endTime}`).join('  ·  ')}</div>
                  </div>
                  <div className="font-semibold text-blue-600">{d.list.reduce((a, s) => a + netHours(s), 0).toFixed(1)}h</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </MobileLayout>
  )
}
